import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import mcbLogo from "../images/mcb-logo.png";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const BOOKINGS_API = `${API_BASE}/bookings`;

function toTitle(v) {
  return String(v || "").replace(/_/g, " ").replace(/-/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
}

export default function BookingPrint() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${BOOKINGS_API}/${id}`, { cache: "no-store" })
      .then((r) => r.json())
      .then((data) => setBooking(data))
      .catch(() => setError("Could not load this booking."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>Loading booking...</div>;
  if (error || !booking) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>{error || "Booking not found."}</div>;

  const sender = booking.onlineRequest?.sender || {};
  const receiver = booking.onlineRequest?.receiver || {};
  const sailing = booking.sailingSnapshot || {};
  const hasLivestock = (booking.livestockItems || []).length > 0;
  const hasFreight = (booking.freightItems || []).length > 0;
  const hasMachinery = booking.machinery && (booking.machinery.description || Number(booking.machinery.weightKg) > 0);

  const sectionTitleStyle = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#7a1428", marginBottom: 10, borderBottom: "1px solid #eee", paddingBottom: 6 };
  const thStyle = { padding: "7px 10px", textAlign: "left", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" };
  const tdStyle = { padding: "6px 10px" };

  return (
    <div style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", background: "#fff", color: "#1a1a1a", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 15mm 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>

      {/* Screen toolbar */}
      <div className="no-print" style={{ background: "#1a1a1a", padding: "12px 32px", display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={() => window.close()} style={{ background: "none", border: "none", color: "#fff", fontSize: 13, opacity: 0.7, cursor: "pointer" }}>← Close</button>
        <button onClick={() => window.print()} style={{ marginLeft: "auto", background: "#7a1428", color: "#fff", border: "none", borderRadius: 6, padding: "8px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Print / Save PDF
        </button>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px 60px" }}>

        {/* ── HEADER ── */}
        <div style={{ borderBottom: "3px solid #7a1428", paddingBottom: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <img src={mcbLogo} alt="McCallum Bros Ltd" style={{ height: 44, width: "auto" }} />
              <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>747 Rosebank Road, Avondale, Auckland</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#1a1a1a" }}>BOOKING CONFIRMATION</div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{booking.bookingRef}</div>
              <div style={{ fontSize: 11, color: "#666" }}>Generated: {fmt(new Date())}</div>
            </div>
          </div>
        </div>

        {booking.customerAlert?.message && (
          <div style={{
            marginBottom: 20, padding: "10px 14px", borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: booking.customerAlert.severity === "critical" ? "#fef2f2" : booking.customerAlert.severity === "warning" ? "#fffbeb" : "#eff6ff",
            border: `1px solid ${booking.customerAlert.severity === "critical" ? "#fca5a5" : booking.customerAlert.severity === "warning" ? "#fbbf24" : "#93c5fd"}`,
            color: booking.customerAlert.severity === "critical" ? "#991b1b" : booking.customerAlert.severity === "warning" ? "#92400e" : "#1e40af",
          }}>
            Notice: {booking.customerAlert.message}
          </div>
        )}

        {/* ── BOOKING DETAILS ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0, marginBottom: 20, border: "1px solid #ddd", borderRadius: 4, overflow: "hidden" }}>
          {[
            { label: "Booking Ref", value: booking.bookingRef },
            { label: "Sailing Ref", value: sailing.sailingRef || booking.requestedSailingRef || "Not yet assigned" },
            { label: "Vessel", value: sailing.vesselName || "TBC" },
            { label: "Route", value: sailing.route || "—" },
            { label: "ETD", value: fmt(sailing.etd) },
            { label: "Status", value: toTitle(booking.status || "pending") },
          ].map((item, i) => (
            <div key={i} style={{ padding: "10px 14px", borderRight: i % 3 < 2 ? "1px solid #ddd" : "none", borderBottom: i < 3 ? "1px solid #ddd" : "none", background: i % 2 === 0 ? "#fafafa" : "#fff" }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#999", marginBottom: 3 }}>{item.label}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* ── SENDER & RECEIVER ── */}
        <div style={{ marginBottom: 24 }}>
          <div style={sectionTitleStyle}>Sender &amp; Receiver</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 6 }}>From</div>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{sender.name || booking.customerName || "—"}</div>
              {sender.company && <div style={{ fontSize: 11, color: "#555" }}>{sender.company}</div>}
              {sender.phone && <div style={{ fontSize: 11, color: "#555" }}>{sender.phone}</div>}
              {sender.email && <div style={{ fontSize: 11, color: "#555" }}>{sender.email}</div>}
              {sender.address && <div style={{ fontSize: 11, color: "#555" }}>{sender.address}</div>}
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 6 }}>To</div>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{receiver.name || "—"}</div>
              {receiver.company && <div style={{ fontSize: 11, color: "#555" }}>{receiver.company}</div>}
              {receiver.phone && <div style={{ fontSize: 11, color: "#555" }}>{receiver.phone}</div>}
              {receiver.email && <div style={{ fontSize: 11, color: "#555" }}>{receiver.email}</div>}
              {receiver.address && <div style={{ fontSize: 11, color: "#555" }}>{receiver.address}</div>}
            </div>
          </div>
        </div>

        {/* ── LIVESTOCK ── */}
        {hasLivestock && (
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>Livestock — Loading Requirements</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
              <thead>
                <tr style={{ background: "#1a1a1a", color: "#fff" }}>
                  {["Type", "Class", "Head", "Avg Weight", "Last Fed", "Last Watered", "Fit for Transport", "Notes"].map((h) => <th key={h} style={thStyle}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {booking.livestockItems.map((item, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #eee" }}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{toTitle(item.livestockType)}</td>
                    <td style={{ ...tdStyle, textTransform: "capitalize" }}>{item.animalClass || "—"}</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{item.headCount?.toLocaleString() || 0}</td>
                    <td style={tdStyle}>{item.averageWeightKg ? `${item.averageWeightKg} kg` : "—"}</td>
                    <td style={tdStyle}>{item.lastFed ? new Date(item.lastFed).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td style={tdStyle}>{item.lastWatered ? new Date(item.lastWatered).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: item.fitForTransport ? "#007a3d" : "#c00" }}>{item.fitForTransport ? "✓ Yes" : "✗ No"}</td>
                    <td style={{ ...tdStyle, color: "#666" }}>{item.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── FREIGHT ── */}
        {hasFreight && (
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>Freight — Loading Requirements</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ background: "#1a1a1a", color: "#fff" }}>
                  {["Description", "Type", "Qty", "Weight", "Cube"].map((h) => <th key={h} style={thStyle}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {booking.freightItems.map((item, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #eee" }}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{item.description || "—"}</td>
                    <td style={tdStyle}>{toTitle(item.freightType)}</td>
                    <td style={tdStyle}>{item.quantity || 0}</td>
                    <td style={tdStyle}>{item.weightKg ? `${item.weightKg} kg` : "—"}</td>
                    <td style={tdStyle}>{item.cubeM3 ? `${item.cubeM3} m³` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── MACHINERY ── */}
        {hasMachinery && (
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>Machinery / Vehicle</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 0, border: "1px solid #ddd", borderRadius: 4, overflow: "hidden" }}>
              {[
                { label: "Description", value: booking.machinery.description || "—" },
                { label: "Make / Model", value: [booking.machinery.make, booking.machinery.model].filter(Boolean).join(" ") || "—" },
                { label: "Weight", value: booking.machinery.weightKg ? `${booking.machinery.weightKg} kg` : "—" },
                { label: "Dimensions (L×W×H)", value: `${booking.machinery.lengthM || 0}m × ${booking.machinery.widthM || 0}m × ${booking.machinery.heightM || 0}m` },
              ].map((item, i) => (
                <div key={i} style={{ padding: "10px 14px", borderRight: i < 3 ? "1px solid #ddd" : "none", background: "#fafafa" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#999", marginBottom: 3 }}>{item.label}</div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── OWNER'S RISK STATEMENT ── */}
        <div style={{ marginBottom: 24, breakInside: "avoid" }}>
          <div style={sectionTitleStyle}>Owner's Risk Statement</div>
          <div style={{
            border: `1px solid ${booking.ownersRiskStatement?.executed ? "#ddd" : "#c00"}`,
            borderRadius: 4,
            padding: "12px 14px",
            background: booking.ownersRiskStatement?.executed ? "#fafafa" : "#fef2f2",
          }}>
            <div style={{ fontSize: 10.5, fontStyle: "italic", color: "#444", lineHeight: 1.5, marginBottom: 10, borderLeft: "2px solid #ccc", paddingLeft: 10 }}>
              "These goods are to be carried at owner's risk. This means that the carrier will not
              pay compensation if the goods are lost or damaged unless he intentionally loses or
              damages them."
            </div>
            {booking.ownersRiskStatement?.executed ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 2 }}>Signed By</div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{booking.ownersRiskStatement.signedName || "—"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 2 }}>Executed At</div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>
                    {booking.ownersRiskStatement.executedAt
                      ? new Date(booking.ownersRiskStatement.executedAt).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })
                      : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#999", marginBottom: 2 }}>Captured Via</div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>
                    {booking.ownersRiskStatement.executedVia === "online_booking"
                      ? "Online Booking"
                      : booking.ownersRiskStatement.executedVia === "staff_manual"
                      ? "Staff (Manual)"
                      : "—"}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 11, fontWeight: 700, color: "#c00" }}>
                ⚠ Not yet executed for this booking.
              </div>
            )}
          </div>
        </div>

        {/* ── NOTES ── */}
        {booking.notes && (
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitleStyle}>Notes</div>
            <div style={{ fontSize: 11, color: "#555", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{booking.notes}</div>
          </div>
        )}

        {/* ── FOOTER ── */}
        <div style={{ marginTop: 24, paddingTop: 12, borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", fontSize: 9, color: "#bbb" }}>
          <span>McCallum Bros Ltd · Booking Confirmation · {booking.bookingRef}</span>
          <span>Generated {new Date().toLocaleString("en-NZ")}</span>
        </div>

      </div>
    </div>
  );
}